import { useState } from "react";
import { Modal, message } from "../../../design-system";
import { activateOrganization, deactivateOrganization } from "../../../common/api/AdminDashboardClient";
import { handleError } from "../../../common/ErrorHandling";
import { Organization, OrganizationStatus } from "../../../types/AdminDashboardTypes";

/**
 * Switches an organization on or off, and hands the updated organization back to
 * the caller to fold into whatever it is holding.
 *
 * Deactivating also switches off the Wristband tenant, locking every member out,
 * so it asks first. Reactivating only undoes that, so it does not.
 *
 * `pendingId` is the org currently changing, so a caller showing one control
 * per row can put the spinner on the right one.
 */
export const useOrganizationStatus = (onUpdated: (org: Organization) => void) => {
    const [pendingId, setPendingId] = useState<number>();

    const toggleStatus = (org: Organization) => {
        const deactivating = org.status === OrganizationStatus.ACTIVE;
        const apply = async () => {
            setPendingId(org.id);
            try {
                const updated = deactivating
                    ? await deactivateOrganization(org.id)
                    : await activateOrganization(org.id);
                onUpdated(updated);
                message.success(
                    deactivating ? "Organization deactivated" : "Organization activated"
                );
            } catch (error) {
                handleError(error);
            } finally {
                setPendingId(undefined);
            }
        };

        if (!deactivating) {
            apply();
            return;
        }
        Modal.confirm({
            title: `Deactivate ${org.name}?`,
            content:
                "Everyone currently signed in is logged out immediately, and its " +
                "Wristband tenant is switched off, so nobody in the organization " +
                "can sign in until it is reactivated.",
            okText: "Deactivate",
            okButtonProps: { danger: true },
            onOk: apply,
        });
    };

    return { toggleStatus, pendingId };
};
